import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UsersListParams } from '../model/users';
import {
  changeUserRole,
  createUser,
  deleteUser,
  fetchUsers,
  setUserActive,
  setUserPassword,
} from './usersApi';

const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: UsersListParams) => [...userKeys.lists(), params] as const,
};

/** Una pagina di utenti; cambiando pagina o filtri resta visibile quella precedente finché arriva la nuova. */
export function useUsersQuery(params: UsersListParams) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => fetchUsers(params),
    placeholderData: keepPreviousData,
  });
}

const useUserMutation = <TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
  });
};

export const useCreateUser = () => useUserMutation(createUser);
export const useChangeUserRole = () => useUserMutation(changeUserRole);
export const useSetUserActive = () => useUserMutation(setUserActive);
export const useSetUserPassword = () => useUserMutation(setUserPassword);
export const useDeleteUser = () => useUserMutation(deleteUser);
